import { useCallback, useContext } from 'react';
import useHttp from './useHttp';
import AuthContext from '../context/authContext';
import { toast } from 'react-toastify';

const BASE = '/api/v1/employeeChat';

// Prefix a backend-relative attachment url with the API base so it can be used
// directly in <img> / download links.
export const buildAbsoluteUrl = relativeUrl =>
  relativeUrl ? `${import.meta.env.VITE_BE_URL}${relativeUrl}` : '';

export const useEmployeeChat = () => {
  const { getAuthHeaders } = useContext(AuthContext);
  const { isLoading, error, sendRequest } = useHttp();

  const getDirectory = useCallback(
    async search => {
      return sendRequest({
        url: `${BASE}/directory`,
        method: 'GET',
        headers: getAuthHeaders(),
        params: search ? { search } : undefined,
      });
    },
    [getAuthHeaders, sendRequest]
  );

  const getConversations = useCallback(async () => {
    return sendRequest({
      url: `${BASE}/conversations`,
      method: 'GET',
      headers: getAuthHeaders(),
    });
  }, [getAuthHeaders, sendRequest]);

  const getOrCreateConversation = useCallback(
    async userId => {
      try {
        return await sendRequest({
          url: `${BASE}/conversations`,
          method: 'POST',
          headers: getAuthHeaders(),
          body: { userId },
        });
      } catch (err) {
        toast.error('Chat konnte nicht geöffnet werden');
        throw err;
      }
    },
    [getAuthHeaders, sendRequest]
  );

  const getConversation = useCallback(
    async conversationId => {
      return sendRequest({
        url: `${BASE}/conversations/${conversationId}`,
        method: 'GET',
        headers: getAuthHeaders(),
      });
    },
    [getAuthHeaders, sendRequest]
  );

  const getMessages = useCallback(
    async (conversationId, beforeSeq) => {
      return sendRequest({
        url: `${BASE}/conversations/${conversationId}/messages`,
        method: 'GET',
        headers: getAuthHeaders(),
        params: beforeSeq !== undefined && beforeSeq !== null ? { beforeSeq } : undefined,
      });
    },
    [getAuthHeaders, sendRequest]
  );

  const sendMessage = useCallback(
    async (conversationId, payload) => {
      try {
        return await sendRequest({
          url: `${BASE}/conversations/${conversationId}/messages`,
          method: 'POST',
          headers: getAuthHeaders(),
          body: payload,
        });
      } catch (err) {
        toast.error('Nachricht konnte nicht gesendet werden');
        throw err;
      }
    },
    [getAuthHeaders, sendRequest]
  );

  const editMessage = useCallback(
    async (conversationId, messageId, text) => {
      try {
        return await sendRequest({
          url: `${BASE}/conversations/${conversationId}/messages/${messageId}`,
          method: 'PATCH',
          headers: getAuthHeaders(),
          body: { text },
        });
      } catch (err) {
        toast.error('Nachricht konnte nicht bearbeitet werden');
        throw err;
      }
    },
    [getAuthHeaders, sendRequest]
  );

  const deleteMessage = useCallback(
    async (conversationId, messageId) => {
      try {
        return await sendRequest({
          url: `${BASE}/conversations/${conversationId}/messages/${messageId}`,
          method: 'DELETE',
          headers: getAuthHeaders(),
        });
      } catch (err) {
        toast.error('Nachricht konnte nicht gelöscht werden');
        throw err;
      }
    },
    [getAuthHeaders, sendRequest]
  );

  const toggleReaction = useCallback(
    async (conversationId, messageId, emoji) => {
      try {
        return await sendRequest({
          url: `${BASE}/conversations/${conversationId}/messages/${messageId}/reactions`,
          method: 'POST',
          headers: getAuthHeaders(),
          body: { emoji },
        });
      } catch (err) {
        toast.error('Reaktion fehlgeschlagen');
        throw err;
      }
    },
    [getAuthHeaders, sendRequest]
  );

  const markRead = useCallback(
    async (conversationId, messageId) => {
      return sendRequest({
        url: `${BASE}/conversations/${conversationId}/read`,
        method: 'POST',
        headers: getAuthHeaders(),
        body: messageId ? { messageId } : {},
      });
    },
    [getAuthHeaders, sendRequest]
  );

  const updateConversationState = useCallback(
    async (conversationId, patch) => {
      try {
        return await sendRequest({
          url: `${BASE}/conversations/${conversationId}`,
          method: 'PATCH',
          headers: getAuthHeaders(),
          body: patch,
        });
      } catch (err) {
        toast.error('Änderung konnte nicht gespeichert werden');
        throw err;
      }
    },
    [getAuthHeaders, sendRequest]
  );

  const getUnreadCount = useCallback(async () => {
    return sendRequest({
      url: `${BASE}/unread-count`,
      method: 'GET',
      headers: getAuthHeaders(),
    });
  }, [getAuthHeaders, sendRequest]);

  // Device-independent chat capabilities (e.g. moderation), resolved by the
  // backend across all device scopes of the user's roles.
  const getPermissions = useCallback(async () => {
    return sendRequest({
      url: `${BASE}/permissions`,
      method: 'GET',
      headers: getAuthHeaders(),
    });
  }, [getAuthHeaders, sendRequest]);

  const uploadAttachment = useCallback(
    async (conversationId, payload) => {
      try {
        return await sendRequest({
          url: `${BASE}/conversations/${conversationId}/attachments`,
          method: 'POST',
          headers: getAuthHeaders(),
          body: payload,
        });
      } catch (err) {
        toast.error('Datei konnte nicht hochgeladen werden');
        throw err;
      }
    },
    [getAuthHeaders, sendRequest]
  );

  return {
    isLoading,
    error,
    getDirectory,
    getConversations,
    getOrCreateConversation,
    getConversation,
    getMessages,
    sendMessage,
    editMessage,
    deleteMessage,
    toggleReaction,
    markRead,
    updateConversationState,
    getUnreadCount,
    getPermissions,
    uploadAttachment,
  };
};

export default useEmployeeChat;
